import { Update } from 'src/shared/types/update';
import { Organization } from 'src/shared/types/user';

import findUserInOrganization from '../../utils/findUserInOrganization';

const CAVELA_AVATAR = '/images/defaultCavela.svg';

/**
 * Avatar for the update modal. Customer-facing updates and questions are
 * Cavela messages, so they use the Cavela logo. The only exception is a
 * customer-started clarification, which uses the author's organization photo
 * when there is one and otherwise falls back to a letter avatar built from
 * the username.
 */
export function getUpdateCreatorAvatar(
  update: Update,
  organization: Organization | null
): string | undefined {
  if (update?.question) {
    return CAVELA_AVATAR;
  }

  if (
    update?.update_type === 'in-progress' ||
    update?.update_type === 'supplier-update'
  ) {
    return CAVELA_AVATAR;
  }

  if (update?.clarification?.is_staff) {
    return CAVELA_AVATAR;
  }

  if (update?.clarification?.from_user_id) {
    const member = findUserInOrganization(
      organization,
      update.clarification.from_user_id
    );

    // Returning undefined is deliberate: UserAvatar then renders the letter
    // avatar from the name instead of an image.
    return member?.avatar_url || undefined;
  }

  return CAVELA_AVATAR;
}

/**
 * Display name for the update modal avatar tooltip, and the source of the
 * letter avatar when there is no photo. Mirrors `getUpdateCreatorAvatar`:
 * Cavela unless this is a customer clarification.
 *
 * Every branch returns a non-empty string — an empty name makes HeroUI's
 * Avatar fall back to its (empty) icon slot, which renders a blank circle.
 */
export function getUpdateCreatorName(update: Update): string {
  if (update?.question) {
    return 'Cavela';
  }

  if (
    update?.update_type === 'in-progress' ||
    update?.update_type === 'supplier-update'
  ) {
    return 'Cavela';
  }

  if (update?.clarification?.is_staff) {
    return 'Cavela';
  }

  if (update?.clarification?.from_user_id) {
    return update.clarification.from_username || 'Cavela';
  }

  return 'Cavela';
}
